import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { Card } from "@/components/ui/card";
import { SEO, buildBreadcrumbSchema } from "@/components/seo/SEO";
import { Building2, Mail, Phone, Globe } from "lucide-react";

const Impressum = () => {
  return (
    <div className="min-h-screen flex flex-col app-surface">
      <SEO
        title="Impressum — Email2Contact (Celsios GmbH)"
        description="Legal notice (Impressum) for Email2Contact under § 5 TMG. Operator: Celsios GmbH, Munich, Germany. HRB 134969. VAT ID DE442429324."
        path="/impressum"
        jsonLd={buildBreadcrumbSchema([
          { name: "Home", path: "/" },
          { name: "Impressum", path: "/impressum" },
        ])}
      />
      <Navbar />
      
      <main className="flex-grow py-16 md:py-24">
        <div className="container px-4 max-w-3xl mx-auto">
          {/* Header */}
          <div className="mb-12">
            <div className="inline-flex items-center gap-2 app-pill app-pill-primary mb-6">
              <Building2 className="w-3.5 h-3.5" />
              <span>Legal Information</span>
            </div>
            <h1 className="font-display text-4xl md:text-5xl font-bold tracking-tight mb-3">
              Impressum
            </h1>
            <p className="text-muted-foreground">
              Information according to § 5 TMG (German Telemedia Act)
            </p>
          </div>

          {/* Company Information */}
          <Card className="app-tile p-8 mb-8">
            <h2 className="font-display text-2xl font-semibold tracking-tight mb-6">Provider</h2>
            <div className="space-y-4 text-lg">
              <div>
                <p className="font-semibold">Celsios GmbH</p>
                <p className="text-muted-foreground">Cosimastrasse 121</p>
                <p className="text-muted-foreground">81925 München</p>
                <p className="text-muted-foreground">Germany</p>
              </div>
            </div>
          </Card>

          {/* Legal Representative */}
          <Card className="app-tile p-8 mb-8">
            <h2 className="font-display text-2xl font-semibold tracking-tight mb-6">Managing Director</h2>
            <p className="text-lg">Justus Wiedemann</p>
          </Card>

          {/* Contact Information */}
          <Card className="app-tile p-8 mb-8">
            <h2 className="font-display text-2xl font-semibold tracking-tight mb-6">Contact</h2>
            <div className="space-y-4">
              <div className="flex items-start gap-3">
                <Mail className="w-5 h-5 text-primary mt-1" />
                <div>
                  <p className="font-semibold mb-1">E-Mail</p>
                  <a href="mailto:support@email2contact.com" className="text-primary hover:underline">
                    support@email2contact.com
                  </a>
                </div>
              </div>
              
              <div className="flex items-start gap-3">
                <Globe className="w-5 h-5 text-primary mt-1" />
                <div>
                  <p className="font-semibold mb-1">Website</p>
                  <a href="https://email2contact.com" className="text-primary hover:underline" target="_blank" rel="noopener noreferrer">
                    https://email2contact.com
                  </a>
                </div>
              </div>
            </div>
          </Card>

          {/* Register Information */}
          <Card className="app-tile p-8 mb-8">
            <h2 className="font-display text-2xl font-semibold tracking-tight mb-6">Company Registration</h2>
            <div className="space-y-3 text-muted-foreground">
              <p><strong className="text-foreground">Legal Form:</strong> Limited Liability Company (GmbH)</p>
              <p><strong className="text-foreground">Register Court:</strong> District Court Frankfurt am Main</p>
              <p><strong className="text-foreground">Commercial Register Number:</strong> HRB 134969</p>
            </div>
          </Card>

          {/* VAT */}
          <Card className="app-tile p-8 mb-8">
            <h2 className="font-display text-2xl font-semibold tracking-tight mb-6">VAT Identification Number</h2>
            <p className="text-muted-foreground">
              VAT identification number according to § 27a UStG: DE442429324
            </p>
          </Card>

          {/* Responsible for Content */}
          <Card className="app-tile p-8 mb-8">
            <h2 className="font-display text-2xl font-semibold tracking-tight mb-6">Responsible for Content</h2>
            <p className="text-muted-foreground">
              Responsible for content according to § 55 Abs. 2 RStV:
            </p>
            <div className="mt-4">
              <p className="font-semibold">Justus Wiedemann</p>
              <p className="text-muted-foreground">Cosimastrasse 121</p>
              <p className="text-muted-foreground">81925 München</p>
              <p className="text-muted-foreground">Germany</p>
            </div>
          </Card>

          {/* Dispute Resolution */}
          <Card className="app-tile p-8 mb-8">
            <h2 className="font-display text-2xl font-semibold tracking-tight mb-6">Dispute Resolution</h2>
            <p className="text-muted-foreground mb-4">
              The European Commission provides a platform for online dispute resolution (ODR):
            </p>
            <a 
              href="https://ec.europa.eu/consumers/odr" 
              className="text-primary hover:underline"
              target="_blank"
              rel="noopener noreferrer"
            >
              https://ec.europa.eu/consumers/odr
            </a>
            <p className="text-muted-foreground mt-4">
              We are not willing or obliged to participate in dispute resolution proceedings before a consumer arbitration board.
            </p>
          </Card>

          {/* Liability */}
          <Card className="app-tile p-8">
            <h2 className="font-display text-2xl font-semibold tracking-tight mb-6">Liability Disclaimer</h2>
            
            <h3 className="text-xl font-semibold mb-3">Liability for Content</h3>
            <p className="text-muted-foreground mb-6">
              As a service provider, we are responsible for our own content on these pages according to § 7 para.1 TMG under general law. However, according to §§ 8 to 10 TMG, we are not obliged to monitor transmitted or stored third-party information or to investigate circumstances that indicate illegal activity. Obligations to remove or block the use of information according to general laws remain unaffected.
            </p>

            <h3 className="text-xl font-semibold mb-3">Liability for Links</h3>
            <p className="text-muted-foreground mb-6">
              Our website contains links to external third-party websites over whose content we have no influence. Therefore, we cannot accept any liability for this external content. The respective provider or operator of the pages is always responsible for the content of the linked pages.
            </p>

            <h3 className="text-xl font-semibold mb-3">Copyright</h3>
            <p className="text-muted-foreground">
              The content and works created by the site operators on these pages are subject to German copyright law. The reproduction, editing, distribution and any kind of use outside the limits of copyright require the written consent of the respective author or creator.
            </p>
          </Card>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default Impressum;
